import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Clock, FileText, Wallet, Megaphone, Info } from "lucide-react";
import api from "../lib/api";
import { formatDateTimeBR } from "../lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const ICONS = { ponto: Clock, relatorio: FileText, financeiro: Wallet, aviso: Megaphone, info: Info };

export const NotificationBell = () => {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const { data } = useQuery({
        queryKey: ["notifications"],
        queryFn: async () => (await api.get("/notifications")).data,
        refetchInterval: 30000,
    });
    const items = data?.items || [];
    const unread = data?.unread || 0;

    const openItem = async (n) => {
        if (!n.read) { await api.post(`/notifications/${n.id}/read`); qc.invalidateQueries({ queryKey: ["notifications"] }); }
        setOpen(false);
        if (n.link) navigate(n.link);
    };
    const markAll = async () => { await api.post("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifications"] }); };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button data-testid="notification-bell" className="relative rounded-xl p-2 text-foreground/70 transition-colors hover:bg-muted">
                    <Bell className="h-5 w-5" />
                    {unread > 0 && (
                        <span data-testid="notification-badge" className="absolute -right-0.5 -top-0.5 flex h-4.5 min-w-[18px] items-center justify-center rounded-full bg-expense px-1 text-[10px] font-bold text-white" style={{ height: 18 }}>
                            {unread > 9 ? "9+" : unread}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 rounded-2xl p-0" data-testid="notification-panel">
                <div className="flex items-center justify-between border-b border-border p-3">
                    <span className="font-heading font-semibold">Notificações</span>
                    {unread > 0 && (
                        <button onClick={markAll} data-testid="mark-all-read" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
                            <CheckCheck className="h-3.5 w-3.5" /> Marcar todas
                        </button>
                    )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                    {items.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Nenhuma notificação.</p>}
                    {items.map((n) => {
                        const Icon = ICONS[n.type] || Info;
                        return (
                            <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-item-${n.id}`} className={`flex w-full items-start gap-3 border-b border-border/50 p-3 text-left transition-colors hover:bg-muted/50 ${!n.read ? "bg-accent/5" : ""}`}>
                                <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${!n.read ? "bg-accent/15 text-accent" : "bg-muted text-muted-foreground"}`}>
                                    <Icon className="h-4 w-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium leading-tight">{n.title}</p>
                                    <p className="mt-0.5 text-xs text-muted-foreground">{n.message}</p>
                                    <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">{formatDateTimeBR(n.created_at)}</p>
                                </div>
                                {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-expense" />}
                            </button>
                        );
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );
};
